<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { handleError } from '@/utils/errors';
import DialogConfirmDelet from "@/components/common/DialogConfirmDelet.vue";
import ButtonNew from "@/components/common/button/ButtonNew.vue";
import { useRouter } from "vue-router";
import BaseTable from "@/components/tables/BaseTable.vue";
import BaseQuickSearch from "@/components/common/Filters/BaseQuickSearch.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import { getAvailableActionsForRoute } from "@/services/api/permissionManagement.js";
import { QrcodeEntity } from "@/entities/QrcodeEntity.js";
import { listQRCodes, deleteQRCode } from "@/services/api/qrcode.js";
import { getToken } from "@/services/api/auth.js";

const { t } = useI18n();
const router = useRouter();
const entity = ref(new QrcodeEntity());
const items = ref([]);
const totalItems = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const searchText = ref("");
const quickSearchConfig = computed(() => entity.value.pages.list.filters.quickSearch);
const actions = ref([]);
const showDialogConfirmDelet = ref(false);
const itemToDelete = ref(null)
const orderBy = ref(null);
const showSvgDialog = ref(false);
const svgContent = ref("");
const svgName = ref("qrcode.svg");

async function fetchData(params = {}) {
	try {
		loading.value = true;
		currentPage.value = params.page ?? currentPage.value;
		if (orderBy.value) params['orderBy'] = orderBy.value.key;
		if (searchText.value) { const field = quickSearchConfig.value.searchBy[0]; params[field] = "%" + searchText.value + "%"; }
		const response = await listQRCodes({ limit: itemsPerPage.value, page: currentPage.value, ...params });
		items.value = response.data.items ?? response.data;
		totalItems.value = response.page;
	} catch (error) { console.error(error); message.error(await handleError(error, t)); }
	finally { loading.value = false; }
}
function onEdit(item) { router.push(entity.value.pages.edit.route?.at(-1).replace(':id', item.id)); }
function onDelete(item) { itemToDelete.value = item; showDialogConfirmDelet.value = true; }
async function confirmDelete() { try { await deleteQRCode(itemToDelete.value.id); await fetchData(); message.success(t('common.messages.success.itemDeletedSuccessfully')); showDialogConfirmDelet.value = false; } catch (e) { message.error(await handleError(e, t)); } }
async function onPageChange(page) { await fetchData({ page }); }
async function onOrderBy(o) { orderBy.value = o; }

async function downloadImage(row, format) {
	try {
		const token = getToken();
		const resp = await window.API_QRCODE.request({
			method: 'get',
			url: `/qrcode/${row.id}/${format}`,
			responseType: 'blob',
			headers: { Authorization: `Bearer ${token}` },
		});
		const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		const base = row.code || row.name || 'qrcode';
		a.download = `${base}.${format}`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	} catch (e) {
		console.error(e);
		message.error(await handleError(e, t));
	}
}

async function previewSvg(row) {
  try {
    const token = getToken();
    const resp = await window.API_QRCODE.request({
      method: 'get',
      url: `/qrcode/${row.id}/svg`,
      responseType: 'text',
      headers: { Authorization: `Bearer ${token}` },
    });
    svgContent.value = typeof resp.data === 'string' ? resp.data : (resp || '');
    const base = row.code || row.name || 'qrcode';
    svgName.value = `${base}.svg`;
    showSvgDialog.value = true;
  } catch (e) {
    console.error(e);
    message.error(await handleError(e, t));
  }
}

function downloadCurrentSvg() {
  if (!svgContent.value) return;
  const blob = new Blob([svgContent.value], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = svgName.value || 'qrcode.svg';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

onMounted(async () => {
	actions.value = await getAvailableActionsForRoute(entity.value.APIRoute);
	if (!actions.value?.list?.length) { message.error(t("common.messages.errors.403")); } else { await fetchData(); }
});
</script>

<template>
	<BasePage :entity="entity">
		<template #default>
			<v-row class="mb-4">
				<v-col>
					<ButtonNew :route="entity.pages.create.route?.at(-1)" />
				</v-col>
				<v-col class="base-quick-search">
					<BaseQuickSearch style="width:100%" :entity="entity" v-if="quickSearchConfig"
						:config="quickSearchConfig" v-model="searchText" :fetchData="fetchData" />
				</v-col>
			</v-row>
			<BaseTable :fetchData="fetchData" :entity="entity" :items="items" :loading="loading"
				:totalItems="totalItems" :currentPage="currentPage" :itemsPerPage="itemsPerPage" :actions="actions"
				@edit="onEdit" @delete="onDelete" @page-change="onPageChange" @onOrderBy="onOrderBy">
				<template #images="{ row }">
					<v-btn size="small" icon variant="text" class="height-button-icon me-1"
						@click="previewSvg(row)" v-tooltip="'SVG'">
						<v-icon color="deep-purple">mdi-vector-square</v-icon>
					</v-btn>
					<!-- <v-btn size="small" icon variant="text" class="height-button-icon me-1" @click="downloadImage(row,'png')" v-tooltip="'PNG'">
						<v-icon color="teal">mdi-file-png-box</v-icon>
					</v-btn>
					<v-btn size="small" icon variant="text" class="height-button-icon" @click="downloadImage(row,'jpg')" v-tooltip="'JPG'">
						<v-icon color="orange">mdi-file-jpg-box</v-icon>
					</v-btn> -->
				</template>
				<template #status="{ row }">
					<span class="trezo-status-badge" :class="row.status ? 'active' : 'inactive'">{{ t('common.labels.' +
						(row.status ? 'active' : 'inactive')) }}</span>
				</template>
			</BaseTable>
			<DialogConfirmDelet v-model="showDialogConfirmDelet" :item="itemToDelete?.name" @confirm="confirmDelete" />

			<v-dialog v-model="showSvgDialog" max-width="640">
				<v-card>
					<v-toolbar density="compact" color="primary" dark>
						<v-toolbar-title>{{ svgName }}</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-btn icon @click="downloadCurrentSvg" v-tooltip="'Download'">
							<v-icon>mdi-download</v-icon>
						</v-btn>
						<v-btn icon @click="showSvgDialog=false" v-tooltip="t('common.labels.close')">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-toolbar>
					<v-card-text>
						<div class="d-flex justify-center pa-4" style="overflow:auto; max-height:70vh;">
							<div v-html="svgContent" />
						</div>
					</v-card-text>
					<v-card-actions class="justify-end">
						<v-btn color="primary" @click="downloadCurrentSvg">Download SVG</v-btn>
						<v-btn variant="text" @click="showSvgDialog=false">{{ t('common.labels.close')}}</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</template>
	</BasePage>
</template>
